import { tick } from "svelte";
import { derived } from "svelte/store";
import { activeProviderConfigStore, providerConfigDraftStore } from "../stores";

const FIELD_MAP = {
  displayedAdvancedOptions: {
    property: "isDisplayed",
    type: "displayed",
  },
  mandatoryFields: { property: "isMandatory", type: "mandatory" },
  protectedFields: { property: "isProtected", type: "protected" },
};

const FIELD_TYPE_TO_KEY = {
  displayed: "displayedAdvancedOptions",
  mandatory: "mandatoryFields",
  protected: "protectedFields",
};

const extractCoreFieldPath = (internalValue, syncAsList = []) => {
  if (!internalValue) return "";

  // Handle JSON format: custom[] ^ {"fieldId": X, "value": "*"}
  const match = internalValue.match(/custom\[\] \^ \{"fieldId": (\d+)/);
  if (match) return match[1];

  // Special handling for ClientCorporation fields - keep the prefix so that rows are split
  if (internalValue.startsWith("ClientCorporation_")) return internalValue;

  if (syncAsList.length) {
    const syncAsPattern = syncAsList.join("|");
    const dynamicRegex = new RegExp(`^(${syncAsPattern})_custom_`);
    return internalValue.replace(dynamicRegex, "");
  }
  return internalValue.replace(/^[^_]+_custom_/, "");
};

const createFieldMappings = (options) => {
  const basicMappings = {};
  const contextualMappings = {};
  const syncAsList = options?.syncAs || [];
  const optionValues = options?.optionValues || [];

  Object.entries(FIELD_MAP).forEach(([key, { type }]) => {
    const optionVal = optionValues.find((opt) => opt.key === key);

    (optionVal?.values || []).forEach((field) => {
      const displayName = field.key;
      const { val } = field;

      basicMappings[displayName] = basicMappings[displayName] || {};
      basicMappings[displayName][type] = val;

      (field.syncAsRestrictions || []).forEach((syncAs) => {
        const corePath = extractCoreFieldPath(val, syncAsList);
        const contextKey = `${displayName}:${corePath}:${syncAs}`;

        contextualMappings[contextKey] = contextualMappings[contextKey] || {
          displayName,
          corePath,
          syncAs,
          mappings: {},
        };
        contextualMappings[contextKey].mappings[type] = val;
      });
    });
  });

  return { basicMappings, contextualMappings };
};

export const fieldOperations = {
  // Used to prevent infinite loops when toggling fields
  isProcessing: false,

  toggleField: async (syncAs, field, type) => {
    if (fieldOperations.isProcessing) return;

    const key = FIELD_TYPE_TO_KEY[type];

    const internalValue = field.mappings[type];
    if (!internalValue) return;

    fieldOperations.isProcessing = true;

    providerConfigDraftStore.update((draft) => {
      draft.choices = draft.choices || {};
      draft.choices[syncAs] = draft.choices[syncAs] || {};

      const currentArray = draft.choices[syncAs][key] || [];
      const fieldExists = currentArray.includes(internalValue);

      // If we're adding a mandatory field, also add the displayed field if it's not already added
      if (type === "mandatory" && !fieldExists) {
        const displayedKey = field.mappings.displayed;
        if (displayedKey) {
          if (!draft.choices[syncAs].displayedAdvancedOptions)
            draft.choices[syncAs].displayedAdvancedOptions = [];

          const displayedArray = draft.choices[syncAs].displayedAdvancedOptions;

          draft.choices[syncAs].displayedAdvancedOptions = [
            ...displayedArray,
            displayedKey,
          ];
        }
      }

      draft.choices[syncAs][key] = fieldExists
        ? currentArray.filter((f) => f !== internalValue)
        : [...currentArray, internalValue];

      return { ...draft };
    });

    // Wait for DOM updates to complete (fixes infinite loop)
    await tick();

    fieldOperations.isProcessing = false;
  },

  setUpdateBehavior: (syncAs, field, value) => {
    providerConfigDraftStore.update((draft) => {
      const protectedKey = field.mappings.protected;
      if (protectedKey) {
        draft.choices = draft.choices || {};
        draft.choices[syncAs] = draft.choices[syncAs] || {};

        const protectedFields = draft.choices[syncAs].protectedFields || [];
        const isProtected = protectedFields.includes(protectedKey);

        if (value === "ifBlank" && !isProtected) {
          draft.choices[syncAs].protectedFields = [
            ...protectedFields,
            protectedKey,
          ];
        }
        if (value === "always" && isProtected) {
          draft.choices[syncAs].protectedFields = protectedFields.filter(
            (f) => f !== protectedKey,
          );
        }
      }
      return draft;
    });
  },
};

const configOptionValues = (config, syncAs, key) => {
  const found = config.optionValues.find((opt) => opt.key === key);
  const values = found?.values || [];
  return values.filter((value) => {
    const restrictions = value.syncAsRestrictions || [];
    return !restrictions.length || restrictions.includes(syncAs);
  });
};

const userChoices = (draft, syncAs, key) => {
  const typeChoices = draft?.choices?.[syncAs] || {};
  return typeChoices[key] || [];
};

const fieldInfoFromMappings = (fieldKey, syncAs, fieldMappings) => {
  // Try to find a display name in the contextual mappings
  const contextualMapping = Object.values(
    fieldMappings.contextualMappings || {},
  ).find((mapping) => Object.values(mapping.mappings).includes(fieldKey));

  if (contextualMapping) {
    return {
      displayName: contextualMapping.displayName,
      mappings: { ...contextualMapping.mappings },
      syncAs: contextualMapping.syncAs,
    };
  }

  // If not found in contextual mappings, try basic mappings
  const basicMappings = fieldMappings.basicMappings || {};

  // Find the first name where values include fieldKey
  const displayName = Object.keys(basicMappings).find((name) =>
    Object.values(basicMappings[name]).includes(fieldKey),
  );

  return displayName
    ? {
        displayName,
        mappings: { ...basicMappings[displayName] },
        syncAs,
      }
    : null;
};

const addFieldsFromOptions = (allFields, config, syncAs) => {
  Object.entries(FIELD_MAP).forEach(([key, { type, property }]) => {
    configOptionValues(config, syncAs, key).forEach((value) => {
      const corePath = extractCoreFieldPath(value.val);
      const fieldId = `${corePath}:${syncAs}`;

      const field = allFields.get(fieldId) || {
        displayName: value.key,
        corePath,
        syncAs,
        mappings: {},
        isDisplayed: false,
        isMandatory: false,
        isProtected: false,
        syncAsRestrictions: value.syncAsRestrictions || [],
      };
      field.mappings[type] = value.val;
      field[property] = true;
      allFields.set(fieldId, field);
    });
  });
};

const addFieldsFromChoices = (allFields, draft, syncAs, fieldMappings) => {
  Object.entries(FIELD_MAP).forEach(([key, { type }]) => {
    userChoices(draft, syncAs, key).forEach((fieldKey) => {
      const corePath = extractCoreFieldPath(fieldKey);
      const choiceFieldId = `${corePath}:${syncAs}`;

      if (allFields.has(choiceFieldId)) {
        const field = allFields.get(choiceFieldId);
        field.mappings[type] = fieldKey;

        if (type === "displayed") field.isDisplayed = true;
        if (type === "mandatory") field.isMandatory = true;
        if (type === "protected") field.isProtected = true;

        return;
      }

      const fieldInfo = fieldInfoFromMappings(fieldKey, syncAs, fieldMappings);

      // Only add the field if we found mapping information
      if (fieldInfo) {
        allFields.set(choiceFieldId, {
          displayName: fieldInfo.displayName,
          corePath,
          syncAs: fieldInfo.syncAs,
          mappings: fieldInfo.mappings,
          isDisplayed: type === "displayed",
          isMandatory: type === "mandatory",
          isProtected: type === "protected",
          syncAsRestrictions: [fieldInfo.syncAs],
        });
      }
    });
  });
};

const processFieldSettings = (config, draft, syncAs, fieldMappings) => {
  const allFields = new Map();
  addFieldsFromOptions(allFields, config, syncAs);
  addFieldsFromChoices(allFields, draft, syncAs, fieldMappings);

  return Array.from(allFields.values())
    .map((field) => {
      const inChoices = (key) => {
        const val = field.mappings[FIELD_MAP[key].type];
        return val && userChoices(draft, syncAs, key).includes(val);
      };

      const fieldMatch = (key) =>
        configOptionValues(config, syncAs, key).find(
          (value) => extractCoreFieldPath(value.val) === field.corePath,
        );

      const protectedFieldMatch = fieldMatch("protectedFields");

      return {
        ...field,
        key: `${field.displayName}:${field.corePath}:${field.syncAs}`,
        label: protectedFieldMatch
          ? protectedFieldMatch.key
          : field.displayName,
        isDisplayed: inChoices("displayedAdvancedOptions"),
        isMandatory: inChoices("mandatoryFields"),
        isProtected: inChoices("protectedFields"),
        existsInProtectedOnly:
          fieldMatch("protectedFields") &&
          !fieldMatch("displayedAdvancedOptions") &&
          !fieldMatch("mandatoryFields"),
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
};

export const fieldSettingsStore = derived(
  [activeProviderConfigStore, providerConfigDraftStore],
  ([$config, $draft]) =>
    (syncAs) => {
      if (!$config?.optionValues) return [];
      const fieldMappings = createFieldMappings($config);
      return processFieldSettings($config, $draft, syncAs, fieldMappings);
    },
);
